import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Row,
  Select,
  Space,
  Statistic,
  Table,
} from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AButton, PageIntro, PageLayout, PagePanel } from '../../components';
import { useToast } from '../../hooks/toast';
import { getWhatsappUsage } from '../../http';
import { formatDate } from '../../shared/utils/format-date';

interface IWhatsappUsage {
  date: string;
  count: number;
  type: string;
}

const cardStyle: React.CSSProperties = {
  borderRadius: 18,
  border: '1px solid rgba(7, 94, 84, 0.12)',
  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
};

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: dayjs().month(index).format('MMMM'),
}));

const currentYear = dayjs().year();
const yearOptions = Array.from({ length: 5 }, (_, index) => ({
  value: currentYear - index,
  label: String(currentYear - index),
}));

const WhatsappUsage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const now = dayjs();
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(now.month() + 1);
  const [year, setYear] = useState(now.year());
  const [totalSent, setTotalSent] = useState(0);
  const [items, setItems] = useState<IWhatsappUsage[]>([]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getWhatsappUsage(month, year);

      setItems(data.items || []);
      setTotalSent(data.total_sent || 0);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao carregar relatório',
        description: 'Não foi possível consultar o uso da API do WhatsApp.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, month, year]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const columns = [
    {
      title: 'Data',
      dataIndex: 'date',
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
    },
    {
      title: 'Quantidade',
      dataIndex: 'count',
    },
  ];

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<WhatsAppOutlined />}
        title="Uso do WhatsApp API"
        description="Acompanhe volume, tipos de mensagens e comportamento mensal da integração oficial com a Meta."
        actions={
          <AButton
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/connections')}
          >
            Voltar
          </AButton>
        }
      />
      <PagePanel>
        <Card style={cardStyle}>
          <Space size="large" wrap style={{ width: '100%' }}>
            <Select
              placeholder="Selecione o mês"
              value={month}
              onChange={setMonth}
              options={monthOptions}
              style={{ width: 150 }}
            />
            <Select
              placeholder="Selecione o ano"
              value={year}
              onChange={setYear}
              options={yearOptions}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => window.print()}
            >
              Imprimir
            </Button>
          </Space>
        </Card>
        <br />
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card hoverable style={cardStyle}>
              <Statistic
                title="TOTAL DE ENVIOS (MÊS)"
                value={totalSent}
                prefix={<WhatsAppOutlined style={{ color: '#25D366' }} />}
                valueStyle={{
                  color: '#25D366',
                  fontSize: '32px',
                  fontWeight: 'bold',
                }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {monthOptions.find((item) => item.value === month)?.label} de{' '}
                {year}
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card hoverable style={cardStyle}>
              <Statistic
                title="MÉDIA DIÁRIA"
                value={totalSent > 0 ? Math.round(totalSent / 30) : 0}
                prefix={<WhatsAppOutlined style={{ color: '#128C7E' }} />}
                valueStyle={{
                  color: '#128C7E',
                  fontSize: '32px',
                  fontWeight: 'bold',
                }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                Mensagens por dia (aproximado)
              </span>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card hoverable style={cardStyle}>
              <Statistic
                title="TIPOS DE MENSAGENS"
                value={items.length}
                prefix={<WhatsAppOutlined style={{ color: '#075E54' }} />}
                valueStyle={{
                  color: '#075E54',
                  fontSize: '32px',
                  fontWeight: 'bold',
                }}
              />
              <Divider style={{ margin: '12px 0' }} />
              <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                Variedade de tipos de mensagens
              </span>
            </Card>
          </Col>
        </Row>
        <br />
        <Card style={cardStyle}>
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(record) => `${record.date}-${record.type}`}
            pagination={false}
            loading={loading}
            bordered
            size="small"
            locale={{
              emptyText: 'Nenhum dado encontrado para o período',
            }}
          />
        </Card>
        <br />
        <Card style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Informações sobre o relatório</h3>
          <ul style={{ color: '#595959' }}>
            <li>Os dados são coletados da API oficial do WhatsApp (Meta).</li>
            <li>
              O relatório mostra o uso de envios de mensagens no mês
              selecionado.
            </li>
            <li>Os dados são atualizados em tempo real conforme a API.</li>
            <li>
              Para mais detalhes, acesse as configurações de integração do
              WhatsApp.
            </li>
          </ul>
        </Card>
      </PagePanel>
    </PageLayout>
  );
};

export default WhatsappUsage;
